/**
 * DeploymentModeChooser — desktop-only "how do you want to connect this
 * database?" modal shown BEFORE either the cloud wizard or the on-prem
 * bridge wizard opens.
 *
 * Why it exists: on desktop we have two viable transports for a DB
 * connector — the cloud (Ando reaches the DB directly) and the on-prem
 * bridge (this Tauri shell relays queries over WSS). The dialects the
 * bridge supports (see ``DB_VENDOR_TO_BRIDGE_DIALECT`` in
 * ``ConnectorDialogsLayer``) all have a valid cloud path too, so the
 * runtime alone can't pick for the user: LAN-only databases MUST go
 * through the bridge, public managed ones SHOULD go through the cloud.
 * This chooser makes the tradeoff explicit at the exact moment the user
 * clicks a DB card.
 *
 * NOT rendered on the web build — the parent (``ConnectorDialogsLayer``)
 * only reaches the chooser branch when ``isEnterpriseDbBridgeAvailable()``
 * returns true, so the web bundle never renders it and the click flows
 * straight to the cloud path unchanged.
 *
 * All strings via Lingui (memory rule
 * ``feedback_client_ui_strings_must_use_lingui``). Lucide icons only
 * (memory rule ``feedback_no_emoji_in_user_facing_strings``). Modal not
 * side-drawer, primary CTA orange (memory rules
 * ``feedback_prefer_modals_over_side_drawers`` +
 * ``feedback_primary_cta_must_stay_visible``).
 */

import { useEffect, useState, type FC, type ReactElement } from "react";

import { Trans } from "@lingui/react/macro";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ando/ui";
import { Cloud, Laptop } from "lucide-react";

export type DeploymentMode = "cloud" | "bridge";

export interface DeploymentModeChooserProps {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  /**
   * Fired when the user confirms a choice via "Continua". Parent then
   * mounts either the cloud ``ConnectAccountDialog`` (``"cloud"``) or the
   * on-prem ``EnterpriseDbBridgeSetupWizard`` (``"bridge"``).
   */
  readonly onSelect: (mode: DeploymentMode) => void;
  /**
   * Which mode is preselected when the chooser opens. Defaults to
   * ``"bridge"`` (the most common desktop case — LAN databases behind a
   * corporate firewall).
   */
  readonly initialMode?: DeploymentMode;
}

// ── Component ───────────────────────────────────────────────────────

export const DeploymentModeChooser: FC<DeploymentModeChooserProps> = ({
  open,
  onOpenChange,
  onSelect,
  initialMode = "bridge",
}) => {
  const [mode, setMode] = useState<DeploymentMode>(initialMode);

  // Reset selection whenever the chooser reopens fresh so a prior run's
  // pick doesn't sticky across cards (same pattern as the bridge wizard's
  // reset-on-open effect).
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Modalità di connessione</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Scegli come Ando raggiungerà il tuo database. Puoi cambiare in
              seguito dalla card del connettore.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <ModeCard
            mode="cloud"
            icon={<Cloud aria-hidden strokeWidth={1.75} className="h-4 w-4" />}
            label={<Trans>Cloud</Trans>}
            description={
              <Trans>
                Ando si connette direttamente al database. Consigliato per
                database pubblici o raggiungibili via internet.
              </Trans>
            }
            checked={mode === "cloud"}
            onSelect={() => setMode("cloud")}
          />
          <ModeCard
            mode="bridge"
            icon={<Laptop aria-hidden strokeWidth={1.75} className="h-4 w-4" />}
            label={<Trans>On-prem Bridge</Trans>}
            description={
              <Trans>
                Questa app fa da ponte verso il database. Consigliato per
                database in rete locale o dietro firewall aziendale.
              </Trans>
            }
            checked={mode === "bridge"}
            onSelect={() => setMode("bridge")}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Annulla</Trans>
          </Button>
          <Button type="button" onClick={() => onSelect(mode)}>
            <Trans>Continua</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
DeploymentModeChooser.displayName = "DeploymentModeChooser";

// ── Small pieces ────────────────────────────────────────────────────

/**
 * A single "big card" radio option. Kept module-scope so React doesn't
 * re-declare the component identity across renders (would blow the
 * children cache). Same visual language as ``DialectRadio`` in
 * ``SetupWizard.tsx`` (orange when checked, hairline card otherwise) so
 * the two modals feel like one flow.
 */
function ModeCard({
  mode,
  icon,
  label,
  description,
  checked,
  onSelect,
}: {
  mode: DeploymentMode;
  icon: ReactElement;
  label: ReactElement;
  description: ReactElement;
  checked: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <label
      className={
        checked
          ? "flex cursor-pointer items-start gap-2.5 rounded-md border border-ando-orange bg-ando-orange/[0.08] px-3 py-3 transition-colors duration-150 ease-out focus-within:ring-2 focus-within:ring-ring"
          : "flex cursor-pointer items-start gap-2.5 rounded-md border border-hairline bg-card px-3 py-3 transition-colors duration-150 ease-out hover:bg-black/[0.04] focus-within:ring-2 focus-within:ring-ring"
      }
    >
      <input
        type="radio"
        name="deployment-mode-chooser"
        className="sr-only"
        value={mode}
        checked={checked}
        onChange={onSelect}
      />
      <span
        aria-hidden
        className={
          checked
            ? "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-ando-orange"
            : "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-hairline"
        }
      >
        {checked ? <span className="h-2 w-2 rounded-full bg-ando-orange" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="inline-flex items-center gap-1.5 font-medium text-ink-1 text-sm leading-5">
          {icon}
          {label}
        </span>
        <span className="text-ink-3 text-xs leading-4">{description}</span>
      </span>
    </label>
  );
}
